<template>
  <div>
    <div class="card text-center bg-green text-white max-w-sm mx-auto p-8 mt-4 mb-4">
      Looking for access to your organization?
      <span
        class="underline"
        v-text="'Check your email'"
      />
      to see if you have a pending invitation.
    </div>
    <div class="card text-center bg-white max-w-sm mx-auto md:p-1 md:p-8 pb-4">
      <h1 v-text="'Register'" />
      <form
        class="form-horizontal"
        @submit.prevent="submit"
      >
        <slot name="honeypot" />
        <div class="w-full px-8 text-left">
          <div class="flex">
            <div class="w-1/2 pr-4">
              <label
                for="first_name"
                v-text="'First Name'"
              />
              <input
                id="first_name"
                v-model="form.first_name"
                name="first_name"
                autofocus
                :class="{ error: form.errors.has('first_name') }"
                type="text"
                required
                placeholder="First Name"
              >
              <span
                v-if="form.errors.has('first_name')"
                class="help-block"
                v-text="form.errors.get('first_name')"
              />
            </div>
            <div class="w-1/2 pl-4">
              <label
                for="last_name"
                v-text="'Last Name'"
              />
              <input
                id="last_name"
                v-model="form.last_name"
                name="last_name"
                :class="{ error: form.errors.has('last_name') }"
                type="text"
                required
                placeholder="Last Name"
              >
              <span
                v-if="form.errors.has('last_name')"
                class="help-block"
                v-text="form.errors.get('last_name')"
              />
            </div>
          </div>
          <div>
            <label
              for="phone_number"
              v-text="'Mobile Number (used for text messaging login codes)'"
            />
            <VuePhoneNumberInput
              id="phone_number"
              v-model="form.phone_number"
              name="phone_number"
              color="#064bc3"
              class="mb-4"
              :translations="{ phoneNumberLabel: 'Mobile Number' }"
              :default-country-code="form.phone_number_country.toUpperCase()"
              no-flags
              no-validator-state
              required
              @update="handlePhoneUpdate"
            />
            <span
              v-if="form.errors.has('phone_number')"
              class="help-block"
              v-text="form.errors.get('phone_number')"
            />
          </div>
          <div>
            <label
              for="email"
              v-text="'E-Mail Address'"
            />
            <input
              id="email"
              v-model="form.email"
              name="email"
              :class="{ error: form.errors.has('email') }"
              type="email"
              required
              placeholder="example@gmail.com"
            >
            <span
              v-if="form.errors.has('email')"
              class="help-block"
              v-html="form.errors.get('email')"
            />
          </div>
          <SecurityQuestionField
            v-model="form.security.question"
            :questions="securityQuestions"
          />
          <div>
            <label
              for="security_answer"
              v-text="'Security Question Answer'"
            />
            <input
              id="security_answer"
              v-model="form.security.answer"
              name="security_answer"
              :class="{ error: form.errors.has('security.answer') }"
              type="text"
            >
            <span
              v-if="form.errors.has('security.answer')"
              class="help-block"
              v-text="form.errors.get('security.answer')"
            />
          </div>
          <div>
            <input
              id="accept_terms"
              v-model="form.accept_terms"
              name="accept_terms"
              type="checkbox"
            >
            <label for="accept_terms">
              I accept the
              <a
                :href="termsRoute"
                v-text="'terms of service'"
              />
            </label>
          </div>
          <span
            v-if="form.errors.has('accept_terms')"
            class="help-block mt-6"
            v-text="form.errors.get('accept_terms')"
          />
        </div>
        <button
          type="submit"
          class="btn mt-8 my-4"
          :class="{ 'load': form.busy }"
          :disabled="form.busy"
          :aria-disabled="form.busy"
          v-text="'Register via Text Message'"
        />
      </form>
      <div class="flex flex-col">
        <a
          :href="signInRoute"
          class="uppercase font-bold w-full pb-2 mt-4"
          v-text="'Already have an account? Click here'"
        />
      </div>
    </div>
  </div>
</template>

<script type="text/babel">
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'
import { mapActions } from 'vuex'
import MUMForm from 'Base/Resources/assets/js/MUMForm'
import SecurityQuestionField from './SecurityQuestionField'

let potFieldName
let potValidFromFieldName

export default {
  name: 'SMSRegisterForm',
  components: {
    SecurityQuestionField,
    VuePhoneNumberInput,
  },
  props: {
    token: {
      type: String,
      required: true,
    },
    termsRoute: {
      type: String,
      required: true,
    },
    signInRoute: {
      type: String,
      required: true,
    },
    postRoute: {
      type: String,
      required: true,
    },
    securityQuestions: {
      type: Array,
      required: true,
    },
    invitedUser: {
      type: Object,
      required: false,
      default: () => ({
        first_name: null,
        last_name: null,
        email: null,
        phone_number: null,
        phone_number_country: 'US',
      }),
    },
  },
  data() {
    return {
      form: new MUMForm({
        first_name: this.invitedUser.first_name,
        last_name: this.invitedUser.last_name,
        email: this.invitedUser.email,
        accept_terms: false,
        phone_number: this.invitedUser.phone_number,
        phone_number_country: this.invitedUser.phone_number_country || 'us',
        security: {
          question: '',
          answer: null,
        },
      }),
    }
  },
  async mounted() {
    await this.$nextTick()

    // This uses a customized version of the Spatie Honeypot to prevent spam
    potFieldName = document.getElementById('potFieldName').innerText
    potValidFromFieldName = document.getElementById('potValidFromFieldName').innerText

    this.$set(this.form, potFieldName, null)
    this.$set(this.form, potValidFromFieldName, null)

    this.form.security.question = this.securityQuestions[0]
  },
  methods: {
    ...mapActions({
      register: 'authSMSRegister',
    }),
    handlePhoneUpdate(data) {
      this.form.phone_number_country = data.countryCode
    },
    async submit() {
      this.form[potFieldName] = document.getElementById(potFieldName).value
      this.form[potValidFromFieldName] = document.getElementsByName(potValidFromFieldName)[0].value

      this.form.errors.clear()

      try {
        if(!this.form.accept_terms) {
          delete this.form.accept_terms
        }

        const { data } = await this.register({
          route: this.postRoute,
          form: this.form,
        })

        window.location = `${data.redirect}?registered`
      } catch (errors) {
        this.form.errors.set(errors.response.data)
      }
    },
  },
}
</script>
